import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import AddBox from '@material-ui/icons/AddBox';
import { slugify } from 'utils/string-helpers';
import LoadingSpinner from 'components/LoadingSpinner';
import { Message } from 'components/shared';
import { ActiveTimeSeriesModal, YearDataTable } from './components';
import { commonColumns } from './utils';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
  header: {
    backgroundColor: theme.palette.primary.dark,
    padding: '.5rem',
    textAlign: 'center',
    color: theme.palette.white,
    fontSize: theme.customStyles.tableRow.fontSize,
    fontWeight: theme.typography.fontWeightBold
  },
  footer: {
    backgroundColor: theme.palette.grey.light,
    padding: '5px 10px',
    borderTop: `1px solid ${theme.palette.grey.medium}`,
    borderBottom: `1px solid ${theme.palette.grey.medium}`
  },
  modalControl: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    verticalAlign: theme.customStyles.addButton.verticalAlign,
    '& svg': {
      fontSize: theme.customStyles.addAttributeSvg.fontSize,
      color: theme.palette.accent.main
    },
    '& span': {
      marginLeft: '5px',
      color: theme.palette.grey.dark,
      fontSize: theme.customStyles.addAttributeSpan.fontSize
    }
  }
}));

const RollingPerformanceTable = ({ widget, state, setTableExportData, forwardRef }) => {
  const classes = useStyles();
  const [activeTimeSeries, setActiveTimeSeries] = useState([]);
  const [maxYear, setMaxYear] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const { data: response, isLoading, error, isError } = state;

  const { overlay } = widget;
  const { widgetTitle } = overlay;

  useEffect(() => {
    if (!response || isLoading || isError) return;

    const latestYear = response.data.reduce((max, item) => {
      const year = moment(item.year, 'YYYY');
      return year.isAfter(max) ? year : max;
    }, moment('0000', 'YYYY'));
    setMaxYear(latestYear.format('YYYY'));

    const exportData = response.data.reduce((acc, yearData) => {
      acc.push([yearData.year, ...commonColumns.map(c => c.title)]);
      const dataRows = response.timeSeries.map((timeSerie, index) => {
        return [
          timeSerie,
          ...yearData.monthlyEntries.map(monthlyEntry => monthlyEntry.values[index])
        ];
      });
      acc.push(...dataRows);
      return acc;
    }, []);

    setActiveTimeSeries(response.timeSeries.map(ts => ({ label: ts, isActive: true })));
    setTableExportData({
      title: widgetTitle && slugify(widgetTitle),
      columns: [],
      rows: exportData
    });
  }, [response, isLoading, isError, setTableExportData, widgetTitle]);

  const handleOpen = () => {
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const onModalSubmit = items => {
    setModalOpen(false);
    setActiveTimeSeries(items);
  };

  if (isError) {
    return <Message type="error" text={error} />;
  }

  if (isLoading || !response) {
    return <LoadingSpinner />;
  }

  const renderYearTables = () =>
    response.data.map(yearData => (
      <YearDataTable key={yearData.year} timeSeries={activeTimeSeries} timeseriesToolTipnames = {response.timeSeriesToolTipNames} data={yearData} maxYear={maxYear} />
    ));

  return (
    <div ref={forwardRef}>
      <div className={classes.header}>Monthly Performance</div>
      {renderYearTables()}
      <div className={classes.footer}>
        <div role="none" className={classes.modalControl} onClick={handleOpen}>
          <AddBox />
          <span>Manage Time Series</span>
        </div>
      </div>
      {modalOpen && (
        <ActiveTimeSeriesModal
          items={activeTimeSeries}
          isOpen={modalOpen}
          onClose={handleClose}
          onSubmit={onModalSubmit}
        />
      )}
    </div>
  );
};

RollingPerformanceTable.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }),
  widget: PropTypes.shape({
    title: PropTypes.string,
    colSpan: PropTypes.number.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dateRangeType: PropTypes.string,
    dateMatchType: PropTypes.number,
    isTitleDisplayed: PropTypes.bool.isRequired,
    isDateDisplayed: PropTypes.bool.isRequired,
    isLegendDisplayed: PropTypes.bool.isRequired,
    isChartTooltipDisplayed: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    masterObjectId: PropTypes.number.isRequired,
    masterObjectType: PropTypes.string,
    masterObjectName: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired
      })
    ),
    masterObjectTypeId: PropTypes.number,
    overlay: PropTypes.shape({
      widgetTitle: PropTypes.string,
      baseWidget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        widgetType: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }).isRequired,
  setTableExportData: PropTypes.func,
  forwardRef: PropTypes.shape({})
};

RollingPerformanceTable.defaultProps = {
  setTableExportData: null,
  state: {},
  forwardRef: null
};

export default RollingPerformanceTable;
